<template>
  <div class="gs-app gm-lead-analytics flex mb-4 mx-4">
    <!-- Loader -->
    <portal to="loaderTarget">
      <div class="absolute bg-grey-100 bg-opacity-50 h-full left-0 top-0 w-full z-99" v-if="isLoading">
        <gd-loader :label="$t('frontend_loading')"/>
      </div>
    </portal>

    <div class="gm-lead-analytics__surface w-full">
      <navigation-tabs
        class="gm-nav-lead-analytics-tabs absolute"
        :nav_tabs="navTabs"
      ></navigation-tabs>
      <server-table
        class="text-center groovemail-lead-analytics-table"
        ref="serverTable"
        :url="contactsListUrl"
        tableRef="leadsTable"
        tableTitle="frontend_groovemail_lead_analytics"
        :slotData="leadAnalyticsSlotData"
        :tableColumns="mailLeadTableColumns"
        :tableOptions="mailLeadTableOptions"
        :multiSelection="true"
        multiSelectUniqueId="lead_id"
        bulkActionName="frontend_affiliates_bulk_actions"
        :entityName="entityName"
        :bulkOptions="bulkActions"
        :emptyStateData="emptyStateData"
        @getSelectionData="selectedData"
        @loaded="hideLoader()"
        @loading="showLoader()"
        @filter="filteredTable"
      >
      </server-table>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import ServerTable from '@groovepages/gd-ui-kit/components/tables/ServerTable.vue';
import NavigationTabs from '@/components/navigation-tabs.vue';
import { contactsUrls, leadUrls } from '@/utils/urls.js';
import downloadCsvFile from '@/components/downloadCsvFile.js';
import demoModeValuesMixin from '@/mixin/demoModeValuesMixin.js';
import { showRequestErrorAlert } from '@/components/showDialog.js';

let self;

export default {
  name: 'LeadAnalytics',
  middleware: 'auth',

  metaInfo() {
    return { title: this.$t('frontend_groovemail_lead_analytics') };
  },

  mixins: [demoModeValuesMixin],

  data() {
    return {
      contactsListUrl: contactsUrls.contactsList,
      isAllSelected: false,
      isLoading: false,
      selectedEnteries: [],
      unselectedEnteries: [],
      entityName: {
        entityNameSingular: 'frontend_groovemail_contact_entity_name_singular',
        entityNamePlural: 'frontend_contacts'
      },
      mailLeadTableColumns: [
        'name',
        'email',
        'phone_number',
        'list_count',
        'tag_count',
        'actions',
      ],
      emptyStateData: {
        emptyStateHeading: 'frontend_groovemail_lead_analytics',
        emptyStateSubHeading: 'frontend_groovemail_lead_analytics_have_no_data',
      },
    };
  },

  components: {
    NavigationTabs,
    ServerTable,
  },

  created(){
    self=this;
  },

  watch: {
    // Used to refresh leads table when demo mode changes
    demoMode() {
      this.$refs.serverTable.$refs.leadsTable.refresh();
    },
  },

  methods: {
    // show loader
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },

    // hide loader
    hideLoader (){
      if (this.isLoading) {
        this.isLoading = false;
      }
    },

    /**
     * Applies search filter on analytics lead table.
     * @param {string} event contains search query.
     */
    filteredTable(event) {
      this.$refs.serverTable.$refs.leadsTable.customQueries.search = event;
    },

    /**
     * Redirects user to contact details page.
     * @param {string} leadId contains unique contacts id.
     */
    goToLeadDetail(leadId) {
      this.$router.push({
        name: 'analyticsLeadDetail',
        params: { id: leadId },
      });
    },

    // Downloads selected contacts on clicking download contacts button in bulk actions dropdown
    async downloadSelectedLeads() {
      const exportQuery = {
        isAllSelected: this.isAllSelected ? 'yes' : 'no',
        selected: this.selectedEnteries,
        unselected: this.unselectedEnteries,
        search: this.$refs.serverTable.$refs.leadsTable.query,
        page: this.$refs.serverTable.$refs.leadsTable.page,
        limit: this.$refs.serverTable.$refs.leadsTable.limit,
        includeType: 'AND',
        excludeType: 'AND',
      };
      
      try {
        // GET /download-lead-csv via leadUrls.downloadLeadCSV.
        // downloadCsvFile requests the streamed text/csv as a blob.
        await downloadCsvFile(
          leadUrls.downloadLeadCSV,
          { params: exportQuery },
          'leads-list.csv',
          ['Name', 'Email']
        );
      } catch (error) {
        // show error message
        showRequestErrorAlert(this.$t('frontend_groovemail_error'), error);
      }
    },

    /**
     * Assigns isAll, selected, and unselected values
     * @param {boolean} isAll contains true if all leads are selected
     * @param {boolean} selected contains true if there are some selected leads
     * @param {boolean} unselected contains true if there are some unselected leads
     */
    selectedData(isAll, selected, unselected) {
      this.isAllSelected = isAll;
      this.selectedEnteries = selected;
      this.unselectedEnteries = unselected;
    },
  },

  computed: {
    // Used to return demoMode value from store.
    demoMode() {
      return this.$store.state.demoMode.isDemoMode;
    },

    /**
     * Returns navigation tabs data for lead analytics page.
     * @return {array} details for navigation tabs for lead analytics page.
    */
    navTabs() {
      return [
          {
            tabName: 'frontend_groovemail_leads_route_page_title',
            tabRoute: 'LeadAnalytics',
          },
          {
            tabName: 'frontend_groovemail_lists_route_page_title',
            tabRoute: 'ListAnalytics',
          },
          {
            tabName: 'frontend_groovemail_broadcasts_route_page_title',
            tabRoute: 'BroadcastAnalytics',
          },
          {
            tabName: 'frontend_groovemail_sequences_route_page_title',
            tabRoute: 'SequenceAnalytics',
          },
        ];
    },

    /**
     * Returns table options for lead analytics table.
     * @return {object} details for table options for lead analytics table.
    */
    mailLeadTableOptions() {
      return {
        perPage: 25,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        pagination: { chunk: 5 },
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        customFilters: ['search'],
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        sortable: [],
        filterable: ['name'],
        headings: {
          check: () => '',
          name: () => this.$t('frontend_groovemail_adminsetting_name'),
          email: () => this.$t('frontend_resource_email'),
          phone_number: () => this.$t('frontend_groovemail_contacts_step2_phone_number'),
          list_count: () => this.$t('frontend_groovemail_mail_lead_show_lists'),
          tag_count: () => this.$t('frontend_groovemail_mail_lead_show_tags'),
          actions: () => this.$t('frontend_affiliate_promos_actions'),
        },
        columnsClasses: {
          name: 'w-1/6',
          email: 'w-1/6',
          phone_number: 'w-1/6',
          list_count: 'w-1/6',
          tag_count: 'w-1/6',
          actions: 'w-1/6',
        },
        rowClassCallback(myRowObject) {
          if (myRowObject.can_email !== 1)
            return 'filter blur pointer-events-none	select-none';
          
          return '';
        },
        requestFunction(data) {
          let self = this;
          return axios
            .get(self.url, {
              params: data,
            })
            .catch((e) => {
              self.dispatch('error', e);
            });
        },
        responseAdapter({ data }) {
          const modifiedData = [];
          if (
            this.$parent.isAllSelected &&
            !this.$parent.unselectedEnteries.length
          ) {
            data.data.forEach((element) => {
              modifiedData.push({ ...element, checked: true });
            });
          } else if (
            this.$parent.isAllSelected &&
            this.$parent.unselectedEnteries.length
          ) {
            data.data.forEach((element) => {
              modifiedData.push({
                ...element,
                checked: !this.$parent.unselectedEnteries.includes(
                  element.lead_id
                ),
              });
            });
          } else {
            data.data.forEach((element) => {
              modifiedData.push({
                ...element,
                checked: this.$parent.selectedEnteries.includes(
                  element.lead_id
                ),
              });
            });
          }

          if(self.demoMode) {
            const newData = data?.data.map((obj) => ({
              ...obj,
              first_name: self.basicText,
              last_name: self.basicText,
              email: self.basicEmailText,
              phone_number: self.basicText,
              company: self.basicText,
              address_1: self.basicText,
              address_2: self.basicText,
              city: self.basicText,
              state: self.basicText,
              country: self.basicText,
              zip: self.basicText,
              url: self.basicUrlText,
              tax_number: self.basicText,
              billing_address: self.basicText,
              ip_address: self.basicIpAddressText }));

            return {
              data: newData,
              count: newData.length,
            };
          }

          return {
            data: modifiedData,
            count: data.total,
          };
        },
      };
    },

    /**
     * Returns navigation tabs data for lead analytics page.
     * @return {array} details for navigation tabs for lead analytics page.
    */
    navTabs() {
      return [
          {
            tabName: 'frontend_groovemail_leads_route_page_title',
            tabRoute: 'LeadAnalytics',
          },
          {
            tabName: 'frontend_groovemail_lists_route_page_title',
            tabRoute: 'ListAnalytics',
          },
          {
            tabName: 'frontend_groovemail_broadcasts_route_page_title',
            tabRoute: 'BroadcastAnalytics',
          },
          {
            tabName: 'frontend_groovemail_sequences_route_page_title',
            tabRoute: 'SequenceAnalytics',
          },
        ];
    },

    /**
     * Returns table options for lead analytics table.
     * @return {object} details for table options for lead analytics table.
    */
    mailLeadTableOptions() {
      return {
        perPage: 25,
        debounce: 1000,
        alwaysShowPerPageSelect: true,
        perPageValues: [10, 25, 50, 100],
        pagination: { chunk: 5 },
        sortIcon: {
          base: 'fa',
          is: 'fa-sort',
          up: 'fa-sort-asc',
          down: 'fa-sort-desc',
        },
        customFilters: ['search'],
        texts: {
          filterPlaceholder: this.$t('frontend_groovemail_tables_search_query'),
        },
        sortable: [],
        filterable: ['name'],
        headings: {
          check: () => '',
          name: () => this.$t('frontend_groovemail_adminsetting_name'),
          email: () => this.$t('frontend_resource_email'),
          phone_number: () => this.$t('frontend_groovemail_contacts_step2_phone_number'),
          list_count: () => this.$t('frontend_groovemail_mail_lead_show_lists'),
          tag_count: () => this.$t('frontend_groovemail_mail_lead_show_tags'),
          actions: () => this.$t('frontend_affiliate_promos_actions'),
        },
        columnsClasses: {
          name: 'w-1/6',
          email: 'w-1/6',
          phone_number: 'w-1/6',
          list_count: 'w-1/6',
          tag_count: 'w-1/6',
          actions: 'w-1/6',
        },
        rowClassCallback(myRowObject) {
          if (myRowObject.can_email !== 1)
            return 'filter blur pointer-events-none	select-none';
          
          return '';
        },
        requestFunction(data) {
          let self = this;
          return axios
            .get(self.url, {
              params: data,
            })
            .catch((e) => {
              self.dispatch('error', e);
            });
        },
        responseAdapter({ data }) {
          const modifiedData = [];
          if (
            this.$parent.isAllSelected &&
            !this.$parent.unselectedEnteries.length
          ) {
            data.data.forEach((element) => {
              modifiedData.push({ ...element, checked: true });
            });
          } else if (
            this.$parent.isAllSelected &&
            this.$parent.unselectedEnteries.length
          ) {
            data.data.forEach((element) => {
              modifiedData.push({
                ...element,
                checked: !this.$parent.unselectedEnteries.includes(
                  element.lead_id
                ),
              });
            });
          } else {
            data.data.forEach((element) => {
              modifiedData.push({
                ...element,
                checked: this.$parent.selectedEnteries.includes(
                  element.lead_id
                ),
              });
            });
          }

          if(self.demoMode) {
            const newData = data?.data.map((obj) => ({
              ...obj,
              first_name: self.basicText,
              last_name: self.basicText,
              email: self.basicEmailText,
              phone_number: self.basicText,
              company: self.basicText,
              address_1: self.basicText,
              address_2: self.basicText,
              city: self.basicText,
              state: self.basicText,
              country: self.basicText,
              zip: self.basicText,
              url: self.basicUrlText,
              tax_number: self.basicText,
              billing_address: self.basicText,
              ip_address: self.basicIpAddressText }));

            return {
              data: newData,
              count: newData.length,
            };
          }

          return {
            data: modifiedData,
            count: data.total,
          };
        },
      };
    },

    /**
     * Returns slot data for lead analytics table.
     * @return {array} details for slot columns for lead analytics table.
    */
    leadAnalyticsSlotData() {
      return [
        {
          slotName: 'name',
          slotType: 'default',
          conditions: () => true,
          classes: 'hover:underline cursor-pointer',
          callback: (props) => this.goToLeadDetail(props.row.lead_id),
          html: (props) => {
            let result = `<span>${this.$t('frontend_groovemail_not_available')}</span>`;
            if (props.row.first_name) {
              if (props.row.last_name) {
                result = `<span>${props.row.first_name} ${props.row.last_name}</span>`;
              } else {
                result = `<span>${props.row.first_name}</span>`;
              }
            }
            return result;
          },
        },
        {
          slotName: 'email',
          slotType: 'computed',
          classes: () => '',
          value: (props) => props.row.email ? props.row.email : this.$t('frontend_groovemail_not_available'),
        },
        {
          slotName: 'phone_number',
          slotType: 'computed',
          classes: () => '',
          value: (props) => props.row.phone_number ? props.row.phone_number : this.$t('frontend_groovemail_not_available'),
        },
        {
          slotName: 'list_count',
          slotType: 'computed',
          classes: () => '',
          value: (props) => props.row.list_count ? props.row.list_count : 0,
        },
        {
          slotName: 'tag_count',
          slotType: 'computed',
          classes: () => '',
          value: (props) => props.row.tag_count ? props.row.tag_count : 0,
        },
        {
          slotName: 'actions',
          slotType: 'actions',
          conditions: () => true,
          classes: () => {
            return {
              outerDiv: '',
              dropdown: '',
              outerSpan: '',
            };
          },
          actionList: [
            {
              id: 0,
              name: () => this.$t('frontend_groovemail_analytics_detailed_stats'),
              conditions: () => true,
              callback: (props) => this.goToLeadDetail(props.row.lead_id),
            },
          ],
        },
      ];
    },

    /**
     * Returns bulk actions options in dropdown if any lead is selected
     * @return {array} data to download details of a details
    */
    bulkActions() {
      const actions = [];
      if (this.isAllSelected || this.selectedEnteries.length) {
        actions.push({
          id: 1,
          name: this.$t('frontend_groovemail_lists_export_contacts'),
          callback: () => this.downloadSelectedLeads(),
        });
      }
      return actions;
    },
  },
};
</script>

<style lang="scss">
.groovemail-lead-analytics-table {
  .VueTables--server {
    th:nth-child(2),
    td:nth-child(2) {
      width: 30%;
      word-break: break-word;
    }
  }
}

.gm-nav-lead-analytics-tabs {
  top: 37px;
  z-index: 10;
}
</style>

<style scoped>
.gm-lead-analytics {
  min-height: 100%;
  padding: 28px;
  color: var(--gs-foreground);
  background: var(--gs-background);
}

.gm-lead-analytics__surface {
  min-width: 0;
  max-width: 1280px;
  margin: 0 auto;
}

.groovemail-lead-analytics-table {
  min-width: 0;
  background: var(--gs-surface);
  border: 1px solid var(--gs-border);
  border-radius: var(--gs-radius-lg);
  box-shadow: var(--gs-shadow-sm);
}

@media (max-width: 640px) {
  .gm-lead-analytics {
    margin-right: 0;
    margin-left: 0;
    padding: 16px;
  }

  .groovemail-lead-analytics-table {
    border-radius: var(--gs-radius-md);
  }
}
</style>
